import { useEffect, useRef } from "react";
import { useApp } from "@/context/AppContext";
import { useFilters } from "@/context/FilterContext";
import type { ViewName } from "@/types";

const VALID_VIEWS: ViewName[] = ["dashboard", "requirements", "tests", "process", "settings", "create-requirement", "create-test", "source-management", "audit-log"];

interface HashState {
  view: ViewName;
  id: string | null;
  project: string | null;
  tree: boolean;
  pageSize: number | null;
}

function parseHash(): HashState {
  const raw = window.location.hash.slice(1); // remove #
  if (!raw) return { view: "requirements", id: null, project: null, tree: false, pageSize: null };

  // Split path from query: "view/id?project=x&tree&page=50"
  const [pathPart, queryPart] = raw.split("?");
  const [viewPart, ...rest] = pathPart.split("/");
  const view = VALID_VIEWS.includes(viewPart as ViewName) ? (viewPart as ViewName) : "requirements";
  const id = rest.join("/") || null;

  // Parse query params
  const params = new URLSearchParams(queryPart || "");
  const project = params.get("project");
  const tree = params.has("tree");
  const pageParam = params.get("page");
  const pageSize = pageParam ? parseInt(pageParam, 10) : null;

  return { view, id, project, tree, pageSize: pageSize && pageSize > 0 ? pageSize : null };
}

function toHash(view: ViewName, id: string | null, project: string | null, tree: boolean, pageSize: number): string {
  let hash = id ? `#${view}/${id}` : `#${view}`;
  const params: string[] = [];
  if (project) params.push(`project=${encodeURIComponent(project)}`);
  if (tree && view === "requirements") params.push("tree");
  if (pageSize !== 10) params.push(`page=${pageSize}`);
  if (params.length) hash += `?${params.join("&")}`;
  return hash;
}

export function useUrlSync() {
  const {
    currentView, activeId, activeProject, treeViewActive,
    navigateTo, setCurrentView, setActiveId, setPanelOpen,
    setTreeViewActive, setActiveProject,
  } = useApp();
  const { pageSize, setPageSize } = useFilters();
  const isPopState = useRef(false);
  const initialLoad = useRef(true);

  // On mount: read hash and set initial state
  useEffect(() => {
    const { view, id, project, tree, pageSize: urlPageSize } = parseHash();
    if (project) setActiveProject(project);
    if (tree) setTreeViewActive(true);
    if (urlPageSize) setPageSize(urlPageSize);
    if (id) {
      navigateTo(view, id);
    } else {
      setCurrentView(view);
    }
    initialLoad.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync state changes to URL (skip if triggered by popstate or initial load)
  useEffect(() => {
    if (initialLoad.current) return;
    if (isPopState.current) {
      isPopState.current = false;
      return;
    }
    const newHash = toHash(currentView, activeId, activeProject || null, treeViewActive, pageSize);
    if (window.location.hash !== newHash) {
      window.history.pushState(null, "", newHash);
    }
  }, [currentView, activeId, activeProject, treeViewActive, pageSize]);

  // Listen for popstate (browser back/forward)
  useEffect(() => {
    function onPopState() {
      isPopState.current = true;
      const { view, id, project, tree, pageSize: urlPageSize } = parseHash();
      if (project) setActiveProject(project);
      setTreeViewActive(tree);
      if (urlPageSize) setPageSize(urlPageSize);
      setCurrentView(view);
      if (id) {
        setActiveId(id);
        setPanelOpen(true);
      } else {
        setActiveId(null);
        setPanelOpen(false);
      }
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [setCurrentView, setActiveId, setPanelOpen, setActiveProject, setTreeViewActive, setPageSize]);
}
